import type { ToolHandle } from '@toolproof-core/genesis/_contract/_index';

import type {
    MutableToolStep,
    MutableToolStepRoleAddress,
} from '@toolproof-core/lib/_contract/workflow-overlay';

import type { ItemBehaviorController } from '@toolproof-core/visualization/_contract/item';

import {
    WORKFLOW_KEYS,
    parseToolStepConstellationKey,
    parseWorkflowStateEntryConstellationKey,
    parseWorkflowStateEntryItemKey,
} from '@/workflow/_pure/keyCodec';
import { collectDraftToolSteps } from '@/workflow-builder/_pure/model/machineWorkflow';
import {
    parseToolHandleFromItemKey,
    parseToolStepKeyFromItemKey,
} from '@/workflow-builder/_pure/interaction/modeAugmentationHelpers';
import type { RuntimeState } from '@/workflow-builder/_contract/runtime';

export type ItemIntent = Parameters<NonNullable<ItemBehaviorController['onIntent']>>[0];

/** Structural so routing stays independent of the derivation sub-area. */
type CommittedToolStepsView = {
    committedToolSteps: readonly MutableToolStep[];
};

type BuilderConstellationFamily =
    | 'tool'
    | 'tool-step'
    | 'workflow-state-entry'
    | 'unknown';

const FAMILY_BY_CONSTELLATION_KEY: Readonly<Record<string, BuilderConstellationFamily>> = {
    [WORKFLOW_KEYS.constellations.tool]: 'tool',
};

export type ItemIntentRoute =
    | { kind: 'tool-selection'; toolHandle: string }
    | { kind: 'tool-step-selection'; stepKey: string }
    | { kind: 'select-input'; roleName: string }
    | { kind: 'bind-internal'; sourceAddress: MutableToolStepRoleAddress }
    | { kind: 'ignore' };

function deriveConstellationFamilyFromIntent(intent: ItemIntent): BuilderConstellationFamily {
    const constellationKey = intent.constellationKey ?? '';
    const itemKey = intent.itemKey ?? '';

    const explicitFamily = FAMILY_BY_CONSTELLATION_KEY[constellationKey];
    if (explicitFamily) {
        return explicitFamily;
    }

    if (parseToolStepConstellationKey(constellationKey)) {
        return 'tool-step';
    }

    if (parseWorkflowStateEntryConstellationKey(constellationKey)) {
        return 'workflow-state-entry';
    }

    // Compatibility fallback for older click events that may omit constellation key.
    if (parseWorkflowStateEntryItemKey(itemKey)) {
        return 'workflow-state-entry';
    }

    return 'unknown';
}

function deriveToolFamilyIntentRoute(args: {
    runtimeState: RuntimeState;
    intent: ItemIntent;
    selectableToolHandleSet: ReadonlySet<ToolHandle>;
}): ItemIntentRoute {
    const { runtimeState, intent, selectableToolHandleSet } = args;

    if (runtimeState.phase !== 'selecting_tool_at_slot') {
        return { kind: 'ignore' };
    }

    if (!intent.itemKey) {
        return { kind: 'ignore' };
    }

    const toolHandle = parseToolHandleFromItemKey(intent.itemKey);
    if (!toolHandle || !selectableToolHandleSet.has(toolHandle)) {
        return { kind: 'ignore' };
    }

    return toolHandle ? { kind: 'tool-selection', toolHandle } : { kind: 'ignore' };
}

function deriveToolStepFamilyIntentRoute(args: {
    runtimeState: RuntimeState;
    intent: ItemIntent;
    allowRenderedToolStepClicksForRebinding: boolean;
}): ItemIntentRoute {
    const { runtimeState, intent, allowRenderedToolStepClicksForRebinding } = args;

    if (!allowRenderedToolStepClicksForRebinding) {
        return { kind: 'ignore' };
    }

    if (
        runtimeState.phase !== 'idle'
        && runtimeState.phase !== 'reviewing_input_bindings'
    ) {
        return { kind: 'ignore' };
    }

    if (!intent.itemKey) {
        return { kind: 'ignore' };
    }

    const stepKey = parseToolStepKeyFromItemKey(intent.itemKey);

    if (
        stepKey
        && runtimeState.phase === 'reviewing_input_bindings'
        && runtimeState.draft
        && (runtimeState.draft.draftKind === 'branch' || runtimeState.draft.draftKind === 'while' || runtimeState.draft.draftKind === 'for')
    ) {
        const draftStepKeySet = new Set<string>(collectDraftToolSteps(runtimeState.draft).map((toolStep) => toolStep.stepKey));
        if (!draftStepKeySet.has(stepKey)) {
            return { kind: 'ignore' };
        }
    }

    return stepKey ? { kind: 'tool-step-selection', stepKey } : { kind: 'ignore' };
}

function deriveWorkflowStateEntryFamilyIntentRoute(args: {
    runtimeState: RuntimeState;
    projectionRuntimeState: CommittedToolStepsView;
    intent: ItemIntent;
}): ItemIntentRoute {
    const { runtimeState, projectionRuntimeState, intent } = args;

    if (runtimeState.phase !== 'binding_inputs' && runtimeState.phase !== 'reviewing_input_bindings') {
        return { kind: 'ignore' };
    }

    if (!intent.itemKey) {
        return { kind: 'ignore' };
    }

    const parsedWorkflowStateEntry = parseWorkflowStateEntryItemKey(intent.itemKey);
    if (!parsedWorkflowStateEntry) {
        return { kind: 'ignore' };
    }

    const selectedToolStep = runtimeState.selectionState.selectedToolStep;
    if (!selectedToolStep) {
        return { kind: 'ignore' };
    }

    if (parsedWorkflowStateEntry.stepKey === selectedToolStep.stepKey) {
        if (parsedWorkflowStateEntry.direction !== 'input') {
            return { kind: 'ignore' };
        }

        return {
            kind: 'select-input',
            roleName: parsedWorkflowStateEntry.roleName,
        };
    }

    const activeDraftStepIndex = projectionRuntimeState.committedToolSteps.findIndex(
        (toolStep) => toolStep.stepKey === selectedToolStep.stepKey,
    );
    const clickedStepIndex = projectionRuntimeState.committedToolSteps.findIndex(
        (toolStep) => toolStep.stepKey === parsedWorkflowStateEntry.stepKey,
    );

    if (clickedStepIndex >= 0 && activeDraftStepIndex >= 0 && clickedStepIndex < activeDraftStepIndex) {
        return {
            kind: 'bind-internal',
            sourceAddress: {
                stepKey: parsedWorkflowStateEntry.stepKey as MutableToolStepRoleAddress['stepKey'],
                roleName: parsedWorkflowStateEntry.roleName,
            },
        };
    }

    return { kind: 'ignore' };
}

export function deriveItemIntentRoute(args: {
    runtimeState: RuntimeState;
    projectionRuntimeState: CommittedToolStepsView;
    intent: ItemIntent;
    allowRenderedToolStepClicksForRebinding: boolean;
    selectableToolHandleSet: ReadonlySet<ToolHandle>;
}): ItemIntentRoute {
    const {
        runtimeState,
        projectionRuntimeState,
        intent,
        allowRenderedToolStepClicksForRebinding,
        selectableToolHandleSet,
    } = args;

    if (intent.itemInteractionIntentKind !== 'item-clicked') {
        return { kind: 'ignore' };
    }

    const family = deriveConstellationFamilyFromIntent(intent);

    switch (family) {
        case 'tool':
            return deriveToolFamilyIntentRoute({ runtimeState, intent, selectableToolHandleSet });
        case 'tool-step':
            return deriveToolStepFamilyIntentRoute({
                runtimeState,
                intent,
                allowRenderedToolStepClicksForRebinding,
            });
        case 'workflow-state-entry':
            return deriveWorkflowStateEntryFamilyIntentRoute({
                runtimeState,
                projectionRuntimeState,
                intent,
            });
        case 'unknown':
        default:
            return { kind: 'ignore' };
    }
}
